<template>
  <RouterLink
    v-if="to && !href && !target"
    :to="to"
    :class="classes"
    :style="iconStyle"
    :aria-label="ariaLabel"
    @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <Tooltip v-if="tooltipText" :text="tooltipText" />
  </RouterLink>
  <a
    v-else-if="href || target"
    :href="href || to"
    :target="target ? '_blank' : undefined"
    :class="classes"
    :style="iconStyle"
    :aria-label="ariaLabel"
    @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <Tooltip v-if="tooltipText" :text="tooltipText" />
  </a>
  <button v-else :disabled="isDisabled" :class="classes" :style="iconStyle" :aria-label="ariaLabel" @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <Tooltip v-if="tooltipText" :text="tooltipText" />
  </button>
</template>

<script lang="ts" setup>
import Tooltip from '@/components/ComponentLibrary/Tooltip.vue';
import { ref, computed, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    disabled?: boolean;
    slim?: boolean;
    // Shrink to fit the label (drops the fixed min-width) instead of the default full width.
    autoWidth?: boolean;
    // Fully rounded (100px) corners instead of the default 4px.
    pill?: boolean;
    to?: string | undefined;
    href?: string | undefined;
    target?: boolean;
    icon?: string | undefined;
    iconPos?: 'left' | 'right' | 'start' | 'end' | 'center' | undefined;
    iconOnly?: boolean;
    iconColour?: string;
    // Theme colour the icon changes to on hover/focus. Lets a destructive action sit quietly
    // (e.g. grey) until you're on it (e.g. error). Falls back to iconColour when unset.
    iconHoverColour?: string;
    tooltip?: string | boolean | undefined;
    delay?: number;
    version?:
      | 'primary'
      | 'secondary'
      | 'highlight'
      | 'action'
      | 'grey'
      | 'white'
      | 'error'
      | 'primary--800'
      | 'transparent'
      | 'outline';
  }>(),
  { label: 'button', disabled: false, slim: false, iconPos: 'left', delay: 2000 }
);

const isDisabled = ref(props.disabled);

// Watch for changes in props.disabled and update isDisabled accordingly
watch(
  () => props.disabled,
  (newVal) => {
    isDisabled.value = newVal;
  }
);

const handleClick = (event: Event) => {
  if ((props.to || props.href || props.target) && props.disabled) {
    event.preventDefault();
    return;
  }

  if (!props.disabled) {
    isDisabled.value = true;
    setTimeout(() => {
      isDisabled.value = props.disabled;
    }, props.delay);
  }
};

const classes = computed(() => ({
  button: true,
  lang_translate: true,
  button__slim: props.slim,
  button__auto: props.autoWidth,
  button__pill: props.pill,
  button__icon: props.iconOnly,
  // Only add iconPos class if not iconOnly
  [`button__icon--${props.iconPos || 'left'}`]: !!props.icon && !props.iconOnly,
  // If iconOnly and no version, default to transparent
  [`button__${props.iconOnly && !props.version ? 'transparent' : props.version || 'primary'}`]: true,
  disabled: (props.to || props.href || props.target) && props.disabled,
}));

const iconStyle = computed(() => {
  const style: Record<string, string> = {};
  if (props.iconColour) style['--button-icon-color'] = `rgb(var(--v-theme-${props.iconColour}))`;
  if (props.iconHoverColour) style['--button-icon-hover-color'] = `rgb(var(--v-theme-${props.iconHoverColour}))`;
  return Object.keys(style).length ? style : undefined;
});

// Icon-only (and centred-icon) buttons hide their label with display: none, which also hides it
// from assistive tech, so the label doubles as the accessible name. A tooltip is a description,
// not a name. An explicit aria-label from the caller still wins (attribute fallthrough).
const ariaLabel = computed(() => (props.iconOnly || props.iconPos === 'center' ? props.label : undefined));

const tooltipText = computed(() => {
  if (typeof props.tooltip === 'string') {
    return props.tooltip;
  } else if (props.tooltip === true) {
    return props.label;
  }
  return '';
});
</script>

<style scoped lang="scss">
//

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  min-width: 280px;
  height: 50px !important;
  color: rgb(var(--v-theme-white));
  background-color: rgb(var(--v-theme-primary));
  font-family: 'brandon-grotesque', sans-serif;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 2px;
  text-align: center;
  border-radius: 4px;
  margin: 0;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  .svg__icon {
    width: 20px;
    height: 20px;
    color: var(--button-icon-color, inherit);
    fill: currentColor;
    margin-right: 12px;
    overflow: hidden;
    pointer-events: none;
    transition: color 0.2s ease;
  }

  // No-op unless iconHoverColour was passed, since it falls back to the resting colour.
  &:hover:not(:disabled) .svg__icon,
  &:focus-visible .svg__icon {
    color: var(--button-icon-hover-color, var(--button-icon-color, inherit));
  }

  &:hover:not(:disabled) {
    background-color: rgb(var(--v-theme-primary-hover));
  }

  &:focus {
    border: 2px solid rgb(var(--v-theme-primary-focus));
  }
  &:disabled,
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &__slim {
    font-size: 11px;
    height: 36px !important;
    min-width: 220px;

    @media (min-width: calc($mobile-screen + 1px)) {
      font-size: 12px;
      height: 40px !important;
    }

    .svg__icon {
      width: 16px;
      height: 16px;
    }
  }

  // Fit-to-content width (overrides the fixed min-width from .button / .button__slim).
  &__auto {
    min-width: auto;
    padding: 0 10px;
  }

  &__pill {
    border-radius: 100px;
  }

  &__secondary {
    color: rgb(var(--v-theme-secondary-text));
    background-color: rgb(var(--v-theme-secondary));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-secondary-hover));
    }
    &:focus {
      border-color: rgb(var(--v-theme-secondary-focus));
    }
  }
  &__highlight {
    color: rgb(var(--v-theme-highlight-text));
    background-color: rgb(var(--v-theme-highlight));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-highlight-hover));
    }
  }
  &__action {
    color: rgb(var(--v-theme-action-text));
    background-color: rgb(var(--v-theme-action));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-action-hover));
    }
  }
  &__grey {
    color: rgb(var(--v-theme-grey-text));
    background-color: rgb(var(--v-theme-grey));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__white {
    color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-white));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__error {
    color: rgb(var(--v-theme-error-text));
    background-color: rgb(var(--v-theme-error));
    &:hover:not(:disabled) {
      background-color: rgba(var(--v-theme-error), 0.8);
    }
  }
  &__primary--800 {
    color: rgb(var(--v-theme-white));
    background-color: rgb(var(--v-theme-primary-800));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-primary-800), 0.8);
    }
  }

  &__transparent {
    color: rgb(var(--v-theme-primary));
    background-color: transparent;
    // border: 2px solid rgb(var(--v-theme-primary));
    &:hover:not(:disabled) {
      background-color: transparent;
      color: rgba(var(--v-theme-primary-hover));
    }
    &:focus {
      border: none;
    }
  }

  &__outline {
    color: rgb(var(--v-theme-primary));
    background-color: transparent;
    border: 2px solid rgb(var(--v-theme-primary));
    min-width: auto;
    &:hover:not(:disabled) {
      background-color: transparent;
      color: rgba(var(--v-theme-primary-hover));
    }
    &:focus {
      border-color: rgb(var(--v-theme-primary-focus));
    }
    @media screen and (min-width: $tablet-screen) {
      min-width: 170px;
    }
  }

  &__icon {
    justify-content: center;
    align-items: center;
    padding: 0;
    min-width: 50px;

    .hide__text {
      display: none;
    }
    .svg__icon {
      margin: 0;
      width: 24px;
      height: 28px;
    }

    &.button__slim {
      min-width: 40px;
      &.button__transparent {
        height: 26px !important;
        width: 26px;
        min-width: 26px;
      }
    }
  }

  &__icon--left .svg__icon {
    margin-right: 12px;
    margin-left: 0;
  }
  &__icon--right {
    flex-direction: row-reverse;
    .svg__icon {
      margin-left: 12px;
      margin-right: 0;
    }
  }
  &__icon--start {
    justify-content: space-between;
    padding: 0 16px;
    .svg__icon {
      margin-right: 12px;
      margin-left: 0;
    }
  }
  &__icon--end {
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: 0 16px;
    .svg__icon {
      margin-left: 12px;
      margin-right: 0;
    }
  }
  &__icon--center {
    font-size: 0;
    justify-content: center;
    .hide__text {
      display: none;
    }
    .svg__icon {
      margin-left: 12px;
      margin-right: 12px;
    }
  }
}
</style>
